import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import type { KpiPeriodType } from "@/features/kpi/repo";

export type KpiActionStatus = "open" | "done";

export interface KpiAction {
  id: string;
  kpiId: string;
  periodType: KpiPeriodType;
  periodStart: string;
  issue: string;
  actionOwner: string;
  dueDate: string;
  status: KpiActionStatus;
}

const LS_KEY = "bb_kpi_actions";
const supabaseReady = () => isSupabaseConfigured && !!supabase;

function missingTable(error: unknown): boolean {
  const value = error as { code?: string; message?: string } | null;
  return (
    value?.code === "42P01" ||
    value?.code === "PGRST205" ||
    Boolean(value?.message?.includes("does not exist"))
  );
}

function localRead(): KpiAction[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) ?? "[]") as KpiAction[];
  } catch {
    return [];
  }
}

function localWrite(actions: KpiAction[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(actions));
}

interface ActionRow {
  id: string;
  kpi_id: string;
  period_type: KpiPeriodType;
  period_start: string;
  issue: string;
  action_owner: string;
  due_date: string | null;
  status: KpiActionStatus;
}

function rowToAction(row: ActionRow): KpiAction {
  return {
    id: row.id,
    kpiId: row.kpi_id,
    periodType: row.period_type,
    periodStart: row.period_start,
    issue: row.issue,
    actionOwner: row.action_owner,
    dueDate: row.due_date ?? "",
    status: row.status,
  };
}

export async function listKpiActions(
  periodType: KpiPeriodType,
  periodStart: string,
): Promise<KpiAction[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("kpi_actions")
      .select("*")
      .eq("period_type", periodType)
      .eq("period_start", periodStart)
      .order("created_at");
    if (!error) return (data as ActionRow[]).map(rowToAction);
    if (!missingTable(error)) throw error;
  }
  return localRead().filter(
    (action) => action.periodType === periodType && action.periodStart === periodStart,
  );
}

export async function saveKpiAction(
  action: Omit<KpiAction, "id"> & { id?: string },
  userId?: string,
): Promise<KpiAction> {
  const record: KpiAction = {
    ...action,
    id: action.id ?? crypto.randomUUID(),
  };
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("kpi_actions")
      .upsert({
        id: record.id,
        kpi_id: record.kpiId,
        period_type: record.periodType,
        period_start: record.periodStart,
        issue: record.issue,
        action_owner: record.actionOwner,
        due_date: record.dueDate || null,
        status: record.status,
        created_by: userId ?? null,
        updated_at: new Date().toISOString(),
      })
      .select("*")
      .single();
    if (!error) return rowToAction(data as ActionRow);
    if (!missingTable(error)) throw error;
  }
  const all = localRead();
  const index = all.findIndex((item) => item.id === record.id);
  if (index >= 0) all[index] = record;
  else all.push(record);
  localWrite(all);
  return record;
}
